import React, { useState, useEffect } from "react";
import { createSessionLink } from "../services/sessionLinkService";
import { getAllTimetableDays } from "../services/adminTimeTableServices";
import "./sessionLinks.css";

const INITIAL = {
  sessionId: "",
  slug: "",
  sourceLabel: "",
  expiresAt: "",
  maxRegistrations: "",
};

const formatSessionLabel = (session) => {
  const time = session.startTime
    ? new Date(session.startTime).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "";
  return `${session.topic} — ${time} IST`;
};

const CreateLinkModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(INITIAL);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadSessions = async () => {
      setLoadingSessions(true);
      try {
        const res = await getAllTimetableDays();
        const days = res.data || [];
        const allSessions = days.flatMap((day) =>
          (day.sessions || []).map((s) => ({ ...s, dayDate: day.date }))
        );
        // Sort by startTime ascending, upcoming first
        allSessions.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
        setSessions(allSessions);
      } catch {
        setSessions([]);
      } finally {
        setLoadingSessions(false);
      }
    };
    loadSessions();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.sessionId) {
      setError("Please select a session.");
      return;
    }

    const payload = {
      sessionId: Number(form.sessionId),
      ...(form.slug && { slug: form.slug }),
      ...(form.sourceLabel && { sourceLabel: form.sourceLabel }),
      ...(form.expiresAt && { expiresAt: new Date(form.expiresAt).toISOString() }),
      ...(form.maxRegistrations && { maxRegistrations: Number(form.maxRegistrations) }),
    };

    setLoading(true);
    const result = await createSessionLink(payload);
    setLoading(false);

    if (result.status === 200 || result.status === 201) {
      onCreated(result.data);
    } else {
      setError(result.message || "Failed to create link.");
    }
  };

  return (
    <div className="sl-modal-overlay" onClick={onClose}>
      <div className="sl-modal" onClick={(e) => e.stopPropagation()}>
        <h3>🔗 Create Session Link</h3>

        <form onSubmit={handleSubmit}>
          <div className="sl-form-group">
            <label>Session *</label>
            {loadingSessions ? (
              <p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0" }}>Loading sessions...</p>
            ) : (
              <select name="sessionId" value={form.sessionId} onChange={handleChange} required>
                <option value="">— Select a session —</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {formatSessionLabel(s)}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="sl-form-group">
            <label>Custom Slug</label>
            <input
              name="slug"
              type="text"
              value={form.slug}
              onChange={handleChange}
              placeholder="e.g. speak-bold-jan"
            />
            <span className="sl-form-hint">Optional. Used in the public URL instead of the token.</span>
          </div>

          <div className="sl-form-group">
            <label>Source Label</label>
            <select name="sourceLabel" value={form.sourceLabel} onChange={handleChange}>
              <option value="">— None —</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="instagram">Instagram</option>
              <option value="email">Email</option>
              <option value="facebook">Facebook</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="sl-form-group">
            <label>Expires At</label>
            <input
              name="expiresAt"
              type="datetime-local"
              value={form.expiresAt}
              onChange={handleChange}
            />
            <span className="sl-form-hint">Optional. Link auto-deactivates after this time.</span>
          </div>

          <div className="sl-form-group">
            <label>Max Registrations</label>
            <input
              name="maxRegistrations"
              type="number"
              value={form.maxRegistrations}
              onChange={handleChange}
              placeholder="e.g. 100"
              min="1"
            />
            <span className="sl-form-hint">Optional. Link auto-closes after this count.</span>
          </div>

          {error && <p className="sl-error">{error}</p>}

          <div className="sl-modal-actions">
            <button type="button" className="sl-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="sl-btn-primary" disabled={loading || loadingSessions}>
              {loading ? "Creating..." : "Create Link"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLinkModal;
